import { api } from "./api";

export interface FlockReportRow {
  date: string;
  age_days: number;
  age_weeks: number;
  mortality: number;
  birds_sold: number;
  total_eggs: number;
  broken_eggs: number;
  feed_kg: number;
  opening_birds: number;
  closing_birds: number;
  cum_mortality: number;
  cum_mortality_pct: number;
  production_trays: number;
  hdp_pct: number;
  feed_per_bird_g: number;
  feed_per_egg_g: number;
  cum_feed_kg: number;
  cum_eggs: number;
  cum_feed_per_egg_g: number;
}

export interface FlockProductionReport {
  flock_id: number;
  batch_name: string;
  arrival_date: string;
  initial_count: number;
  rows: FlockReportRow[];
}

export interface FlockDetailSummary {
  flock_id: number;
  batch_name: string;
  breed_name: string;
  shed_code: string;
  // Where the birds ARE. A layer flock can sit in a brooder shed, and
  // context-dependent UI keys on this rather than on flock_type.
  shed_type?: string;
  flock_type: string;
  status: string;
  age_weeks: number;
  arrival_date: string;
  shifting_date?: string;
  egg_start_date?: string;
  expected_culling: string;
  initial_count: number;
  current_count: number;
  total_mortality_pct: number;
  avg_2_week_hdp_pct: number;
  current_hdp_pct: number;
  avg_2_week_feed_per_bird_g: number;
  // Cumulative eggs / birds HOUSED. Null for an onboarded flock, whose
  // initial count and recorded eggs belong to different cycles.
  eggs_per_hen_housed?: number | null;
  // kg feed per dozen eggs. Null before any eggs.
  feed_per_dozen_kg?: number | null;
  // Share of the birds housed still alive. Null for an onboarded flock.
  livability_pct?: number | null;
}

/**
 * One AGE week — not a calendar week.
 *
 * Age, because that is the axis a breed standard is published on. A flock
 * placed on a Wednesday has its week boundaries on Wednesdays.
 */
export interface FlockWeeklyRow {
  age_weeks: number;
  from: string;
  to: string;
  /**
   * How many days in the week carry any entry. It says which columns can be
   * trusted: a three-day week has a real HDP — a rate over the days that
   * exist — and an egg TOTAL that is three sevenths of a week.
   */
  days_recorded: number;
  opening_birds: number;
  /** Null when no day in the week recorded mortality — as against zero deaths. */
  deaths: number | null;
  mortality_pct: number | null;
  /** Null when no day carried an egg record — as against a counted zero. */
  eggs: number | null;
  /** Eggs / bird-days over the days eggs were recorded. Null if none were. */
  hdp_pct: number | null;
  std_hdp_pct: number | null;
  /** Actual − standard. Null whenever either side is. */
  hdp_gap: number | null;
  feed_per_bird_g: number | null;
  std_feed_per_bird_g: number | null;
  egg_weight_g: number | null;
  std_egg_weight_g: number | null;
  body_weight_g: number | null;
  std_body_weight_g: number | null;
}

export interface FlockWeeklyReport {
  flock_id: number;
  batch_name: string;
  breed_id?: number | null;
  /** Where each standard came from: a breed guide, the shipped default, or none. */
  curve_sources: Record<string, string>;
  /**
   * The flock was entered mid-cycle, so its bird counts are only as complete
   * as the history this app saw: every rate reads low and the gap reads
   * negative on a flock that may be on target.
   */
  onboarded: boolean;
  rows: FlockWeeklyRow[];
}

export const flockReportService = {
  getWeeklyPerformance: (flockId: number): Promise<FlockWeeklyReport> =>
    api.get(`/flocks/${flockId}/weekly-performance`).then((r) => r.data as FlockWeeklyReport),
  getProductionReport: (
    flockId: number,
    from?: string,
    to?: string
  ): Promise<FlockProductionReport> => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();
    return api
      .get(
        `/flocks/${flockId}/production-report${qs ? `?${qs}` : ""}`
      )
      .then((r) => r.data as FlockProductionReport);
  },
  getFlockDetailSummary: (flockId: number): Promise<FlockDetailSummary> =>
    api.get(`/flocks/${flockId}/detail-summary`).then((r) => r.data as FlockDetailSummary),
};
